/**
 * How a trigger reads in a table: icon + label (+ a longer summary).
 * Works from the list row's `trigger` ({type, provider, provider_event_name,
 * cron_expression, cron_timezone}) and from a run's `trigger_type`.
 */
import { Bell, CalendarClock, Webhook, Hand, Zap } from 'lucide-react';
import { parseCron, describeSchedule } from '../WorkflowEditor/scheduleCron';

export const TRIGGER_META = {
  alert:             { label: 'Alert',    Icon: Bell },
  schedule:          { label: 'Schedule', Icon: CalendarClock },
  webhook:           { label: 'Webhook',  Icon: Webhook },
  manual:            { label: 'Manual',   Icon: Hand },
  integration_event: { label: 'Event',    Icon: Zap },
};

const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/**
 * { type, label, Icon } for a trigger type. For integration events pass the
 * provider/event ("Slack: message posted").
 */
export function triggerMeta(type, { provider, eventName } = {}) {
  const base = TRIGGER_META[type] || { label: type ? cap(String(type).replace(/_/g, ' ')) : '', Icon: Zap };
  if (type === 'integration_event' && provider) {
    const ev = eventName ? String(eventName).replace(/_/g, ' ') : 'event';
    return { type, Icon: base.Icon, label: `${cap(provider)}: ${ev}` };
  }
  return { type, Icon: base.Icon, label: base.label };
}

/** triggerMeta for a list row's `trigger` object (null when it has none). */
export function triggerMetaOf(trigger) {
  if (!trigger || !trigger.type) return null;
  return triggerMeta(trigger.type, { provider: trigger.provider, eventName: trigger.provider_event_name });
}

/**
 * One line of detail: the schedule in words, "2 alerts", the alert's name.
 * `extra` may carry { alertName, alertCount }.
 */
export function triggerSummary(trigger, extra = {}) {
  if (!trigger) return '';
  if (trigger.type === 'schedule' && trigger.cron_expression) {
    try {
      return describeSchedule(parseCron(trigger.cron_expression), trigger.cron_timezone || 'UTC').replace(/^Runs /, '');
    } catch (_) { return trigger.cron_expression; }
  }
  if (trigger.type === 'alert') {
    if (extra.alertName) return extra.alertName;
    if (extra.alertCount > 1) return `${extra.alertCount} alerts`;
  }
  return triggerMeta(trigger.type, { provider: trigger.provider, eventName: trigger.provider_event_name }).label;
}

/** Label for a run's trigger: Alert name when known, else the type. */
export function runTriggerLabel(run) {
  if (!run) return '';
  const meta = triggerMeta(run.trigger_type);
  return run.trigger_type === 'alert' && run.trigger_name ? `${meta.label}: ${run.trigger_name}` : meta.label;
}
